import React, { useState, useEffect, useCallback } from "react";
import { HiChevronDown, HiPencil } from "react-icons/hi2";
import { PerformanceLog, Session, TrainingLog } from "../../types/post";
import {
  useLogCardActions,
  LogCardActionsProps,
} from "../../hooks/useLogCardActions";
import LogCard from "../shared/LogCard";
import {
  LogCardChip,
  LogCardMetricsRow,
  LogCardMetric,
} from "../shared/LogCardContent";

import { useAuth } from "../../contexts/AuthContext";
import { sessionService } from "../../services/sessionService";
import { activityService } from "../../services/activityService";
import { getTrainingLog } from "../../services/dayPostService";
import { getPrimarySessionID } from "../../utils/sessionLinks";
import { ActivityType, ActivityVariable } from "../../types/activity";
import { formatMeasurementWithUnit } from "../../utils/unitUtils";
import TrainingLogForm from "./training-log/TrainingLogForm";

interface PerformanceLogCardProps extends LogCardActionsProps<
  PerformanceLog & { id: string }
> {
  log: PerformanceLog & { id: string };
  userId?: string;
}

const PerformanceLogCard: React.FC<PerformanceLogCardProps> = ({
  log,
  onDelete,
  onEdit,
  userId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [linkedSession, setLinkedSession] = useState<Session | null>(null);
  const [activityType, setActivityType] = useState<ActivityType | null>(null);
  const [linkedTrainingLog, setLinkedTrainingLog] =
    useState<TrainingLog | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingTrainingLog, setLoadingTrainingLog] = useState(false);
  const [showEditTrainingLog, setShowEditTrainingLog] = useState(false);
  const { currentUser } = useAuth();

  const { handleDelete, handleEdit } = useLogCardActions(log, {
    onDelete,
    onEdit,
  });

  const handleCardClick = () => {
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    const fetchLinkedSession = async () => {
      // New logs carry the activity directly; only fetch for legacy logs
      if (!log.linkedSessionID || !userId || log.activity) return;

      try {
        setLoadingSession(true);
        const session = await sessionService.getSession(
          userId,
          log.linkedSessionID,
        );
        setLinkedSession(session);

        if (
          session?.ActivityMeasurementList &&
          session.ActivityMeasurementList.length > 0
        ) {
          const firstActivity = session.ActivityMeasurementList[0];
          const types = await activityService.fetchActivityTypes(userId);
          const type = types.find((t) => t.id === firstActivity.ActivityTypeID);
          setActivityType(type || null);
        }
      } catch (error) {
        console.error("Error fetching linked session:", error);
      } finally {
        setLoadingSession(false);
      }
    };

    fetchLinkedSession();
  }, [log.linkedSessionID, userId]);

  // Fetch linked training log
  useEffect(() => {
    const fetchLinkedTrainingLog = async () => {
      if (!log.linkedTrainingLogID || !userId) return;

      try {
        setLoadingTrainingLog(true);
        const trainingLog = await getTrainingLog(
          userId,
          log.linkedTrainingLogID,
        );
        setLinkedTrainingLog(trainingLog);
      } catch (error) {
        console.error("Error fetching linked training log:", error);
      } finally {
        setLoadingTrainingLog(false);
      }
    };

    fetchLinkedTrainingLog();
  }, [log.linkedTrainingLogID, userId]);

  const getProgressColor = (value: number) => {
    if (value <= 2) return "bg-blue-500";
    if (value <= 4) return "bg-green-500";
    if (value <= 6) return "bg-orange-600";
    if (value <= 8) return "bg-orange-700";
    if (value <= 9) return "bg-red-500";
    return "bg-red-700";
  };

  const renderProgressBar = (
    label: string,
    value: number,
    maxValue: number = 10,
  ) => (
    <div className="mb-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[11px] text-base-content/60 font-geist">
          {label}
        </span>
        <span className="text-[11px] font-geist text-base-content">
          <strong>{value}</strong>/{maxValue}
        </span>
      </div>
      <div className="w-full bg-base-300 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor(value)}`}
          style={{ width: `${(value / maxValue) * 100}%` }}
        />
      </div>
    </div>
  );

  // Activity display values: prefer the denormalized `activity` on the log,
  // falling back to a legacy linked session for older logs.
  const activityName: string | null =
    log.activity?.activityName || activityType?.name || null;
  const activityVariables: ActivityVariable[] =
    log.activity?.variables ||
    linkedSession?.ActivityMeasurementList?.[0]?.variables ||
    [];
  const measuredVariables = activityVariables.filter(
    (v) => v.measurement != null,
  );

  return (
    <div onClick={handleCardClick} className="cursor-pointer">
      {/* Edit Training Log Modal */}
      {showEditTrainingLog && linkedTrainingLog && (
        <TrainingLogForm
          open={showEditTrainingLog}
          onClose={() => setShowEditTrainingLog(false)}
          context={{ dateID: log.dateID }}
          editMode
          logId={log.linkedTrainingLogID}
          initialData={{
            dateID: linkedTrainingLog.dateID,
            muscular: linkedTrainingLog.muscular,
            respiratory: linkedTrainingLog.respiratory,
            notes: linkedTrainingLog.notes,
            tag: linkedTrainingLog.tag,
            trainingTypeID: linkedTrainingLog.trainingTypeID,
            duration: linkedTrainingLog.duration,
            trainingPerformed: linkedTrainingLog.trainingPerformed,
            pain: linkedTrainingLog.pain,
            injured: linkedTrainingLog.injured,
            linkedSessionIDs: linkedTrainingLog.linkedSessionIDs,
            linkedSessionID: getPrimarySessionID(linkedTrainingLog),
            linkedActivityLogID: linkedTrainingLog.linkedActivityLogID,
          }}
          onSaved={async () => {
            setShowEditTrainingLog(false);
            if (currentUser?.uid && log.linkedTrainingLogID) {
              const updated = await getTrainingLog(
                currentUser.uid,
                log.linkedTrainingLogID,
              );
              setLinkedTrainingLog(updated);
            }
          }}
        />
      )}
      <LogCard
        className={isExpanded ? "bg-base-100" : "bg-yellow-500/10"}
        onDelete={onDelete ? handleDelete : undefined}
        onEdit={onEdit ? handleEdit : undefined}
      >
        {/* Header */}
        <div className="flex items-center gap-2 pr-8">
          <span className="text-[11px] font-medium tracking-wide uppercase text-base-content/70 font-geist">
            Performance
          </span>
          <HiChevronDown
            className={`w-3 h-3 text-base-content/30 transition-transform duration-200 ml-auto ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Activity name + values + PB/SB tags */}
        {activityName && (
          <div className="text-sm text-base-content font-geist mt-1 flex flex-wrap items-center gap-1.5">
            <span className="font-semibold">{activityName}</span>
            {measuredVariables.map((v, i) => (
              <span key={i} className="text-base-content/70">
                {formatMeasurementWithUnit(v.measurement ?? null, v.unit, v.name)}
              </span>
            ))}
            {log.tag &&
              log.tag.length > 0 &&
              log.tag.map((tag, index) => (
                <span
                  key={index}
                  className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-700 font-geist"
                >
                  {tag}
                </span>
              ))}
          </div>
        )}

        {/* Title + result always visible */}
        <div className={activityName ? "" : "mt-1"}>
          {(log.performanceTitle || log.performanceResult) && (
            <p className="text-[11px] text-base-content/50 font-geist">
              {log.performanceTitle && <span>{log.performanceTitle}</span>}
              {log.performanceTitle && log.performanceResult && (
                <span className="text-base-content/30 mx-1">·</span>
              )}
              {log.performanceResult && <span>{log.performanceResult}</span>}
            </p>
          )}

          {/* Show PB/SB tags here if no activity */}
          {!activityName && log.tag && log.tag.length > 0 && (
            <div className="flex items-center gap-1 mt-1">
              {log.tag.map((tag, index) => (
                <span
                  key={index}
                  className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-700 font-geist"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {log.notes && (
            <p className="text-[13px] text-base-content/70 font-grotesk leading-relaxed mt-1">
              {log.notes}
            </p>
          )}
        </div>

        {isExpanded && (
          <div className="mt-3 space-y-2">
            {/* Performance rating */}
            <div className="flex items-center justify-between bg-base-200/80 rounded-md px-3 py-2">
              <span className="text-[11px] text-base-content/60 font-geist">
                Rating
              </span>
              <span className="text-sm font-bold font-geist text-base-content">
                {log.performanceExpectation}/10
              </span>
            </div>

            {/* Activity details (new logs store the activity directly) */}
            {log.activity && measuredVariables.length > 0 && (
              <div className="bg-base-200/60 rounded-lg p-3">
                <span className="text-[11px] font-semibold tracking-wide uppercase text-base-content/60 font-geist">
                  Activity
                </span>
                <div className="mt-1.5">
                  <p className="text-sm font-semibold text-base-content">
                    {activityName}
                  </p>
                  <div className="mt-2 space-y-0">
                    {measuredVariables.map((variable, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center text-xs py-1.5 border-b border-base-300/50 last:border-0"
                      >
                        <span className="text-base-content/60 font-geist">
                          {variable.name}
                        </span>
                        <span className="font-bold text-base-content font-geist">
                          {variable.measurement !== null &&
                          variable.measurement !== undefined
                            ? formatMeasurementWithUnit(
                                variable.measurement,
                                variable.unit,
                                variable.name,
                              )
                            : "\u2014"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Linked Training Log Details */}
            {log.linkedTrainingLogID && (
              <div className="bg-base-200/60 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold tracking-wide uppercase text-base-content/60 font-geist">
                    Linked Training
                  </span>
                  {linkedTrainingLog && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowEditTrainingLog(true);
                      }}
                      className="flex items-center gap-1 text-[11px] text-primary/70 hover:text-primary transition-colors"
                    >
                      <HiPencil className="w-3 h-3" />
                      Edit
                    </button>
                  )}
                </div>
                {loadingTrainingLog ? (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="animate-spin rounded-full h-3 w-3 border border-base-300 border-t-primary" />
                    <span className="text-xs text-base-content/50 font-grotesk">
                      Loading...
                    </span>
                  </div>
                ) : linkedTrainingLog ? (
                  <div className="mt-1.5 space-y-1.5">
                    {/* Tags */}
                    {linkedTrainingLog.tag &&
                      linkedTrainingLog.tag.length > 0 && (
                        <LogCardMetricsRow gap={1}>
                          {linkedTrainingLog.tag.map((tag, index) => (
                            <LogCardChip key={index} label={tag} />
                          ))}
                        </LogCardMetricsRow>
                      )}

                    {/* Progress bars */}
                    <div className="bg-base-200 rounded-lg p-3 space-y-1">
                      {renderProgressBar(
                        "Muscular",
                        linkedTrainingLog.muscular,
                      )}
                      {renderProgressBar(
                        "Respiratory",
                        linkedTrainingLog.respiratory,
                      )}
                    </div>

                    <LogCardMetric
                      value={linkedTrainingLog.load}
                      label="Load"
                    />
                    <LogCardMetric value={linkedTrainingLog.rpe} label="RPE" />
                    <LogCardMetric
                      value={`${linkedTrainingLog.duration}min`}
                      label="Duration"
                    />
                    {linkedTrainingLog.trainingPerformed !== undefined && (
                      <LogCardMetric
                        value={linkedTrainingLog.trainingPerformed}
                        label="Performance"
                      />
                    )}

                    {/* Training log notes */}
                    {linkedTrainingLog.notes && (
                      <p className="text-[13px] text-base-content/70 font-grotesk leading-relaxed mt-1">
                        {linkedTrainingLog.notes}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-base-content/40 font-grotesk mt-1">
                    Training log not found
                  </p>
                )}
              </div>
            )}

            {/* Linked Session Details */}
            {log.linkedSessionID && (
              <div className="bg-base-200/60 rounded-lg p-3">
                <span className="text-[11px] font-semibold tracking-wide uppercase text-base-content/60 font-geist">
                  Linked Activity
                </span>
                {loadingSession ? (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="animate-spin rounded-full h-3 w-3 border border-base-300 border-t-primary" />
                    <span className="text-xs text-base-content/50 font-grotesk">
                      Loading...
                    </span>
                  </div>
                ) : linkedSession && activityType ? (
                  <div className="mt-1.5">
                    <p className="text-sm font-semibold text-base-content">
                      {activityType.name}
                    </p>
                    {linkedSession.ActivityMeasurementList &&
                      linkedSession.ActivityMeasurementList.length > 0 && (
                        <div className="mt-2 space-y-0">
                          {linkedSession.ActivityMeasurementList[0].variables.map(
                            (variable, index) => (
                              <div
                                key={index}
                                className="flex justify-between items-center text-xs py-1.5 border-b border-base-300/50 last:border-0"
                              >
                                <span className="text-base-content/60 font-geist">
                                  {variable.name}
                                </span>
                                <span className="font-bold text-base-content font-geist">
                                  {variable.measurement !== null &&
                                  variable.measurement !== undefined
                                    ? formatMeasurementWithUnit(
                                        variable.measurement,
                                        variable.unit,
                                        variable.name,
                                      )
                                    : "\u2014"}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                  </div>
                ) : (
                  <p className="text-xs text-base-content/40 font-grotesk mt-1">
                    Session not found
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </LogCard>
    </div>
  );
};

export default PerformanceLogCard;
